import Plyr from 'plyr';
import { CSSProperties, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { detectMimeType } from '../helpers/media.utils';

export interface VideoSegment {
    src: string;
    duration: number;
}

interface SegmentedVideoPlayerProps {
    segments: VideoSegment[];
    className?: string;
    style?: CSSProperties;
    controls?: boolean;
    muted?: boolean;
    keyboard?: boolean;
    poster?: string;
}

function segmentIndexAt(time: number, cumulativeStarts: number[]): number {
    let lo = 0;
    let hi = cumulativeStarts.length - 1;

    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (cumulativeStarts[mid] <= time) lo = mid;
        else hi = mid - 1;
    }

    return lo;
}

async function appendBuffer(sb: SourceBuffer, buf: ArrayBuffer): Promise<void> {
    return new Promise((resolve, reject) => {
        const handleUpdateEnd = () => resolve();
        const handleError = (event: Event) => reject(event);

        sb.addEventListener('updateend', handleUpdateEnd, { once: true });
        sb.addEventListener('error', handleError, { once: true });

        try {
            sb.appendBuffer(buf);
        } catch (err) {
            sb.removeEventListener('updateend', handleUpdateEnd);
            sb.removeEventListener('error', handleError);
            reject(err);
        }
    });
}

async function waitForIdle(sb: SourceBuffer): Promise<void> {
    if (!sb.updating) return;

    await new Promise<void>((resolve) => {
        sb.addEventListener('updateend', () => resolve(), { once: true });
    });
}

const SegmentedVideoPlayer = forwardRef<HTMLVideoElement, SegmentedVideoPlayerProps>((props, ref) => {
    const { controls = true, muted = false, keyboard = false, segments, className, style, poster } = props;
    const videoRef = useRef<HTMLVideoElement>(null);

    useImperativeHandle(ref, () => videoRef.current!, []);

    useEffect(() => {
        if (!videoRef.current || segments.length === 0) return;

        const videoEl = videoRef.current;
        const controlsList = controls
            ? ['play-large', 'play', 'progress', 'current-time', 'duration', 'mute', 'volume', 'settings', 'fullscreen']
            : [];

        const player = new Plyr(videoEl, {
            controls: controlsList,
            keyboard: { global: keyboard },
        });

        player.muted = muted;
        if (poster) player.poster = poster;

        const cumulativeStarts: number[] = [];
        let totalDuration = 0;
        for (const seg of segments) {
            cumulativeStarts.push(totalDuration);
            totalDuration += seg.duration;
        }

        let destroyed = false;
        let cleanupPlaybackListeners = () => {};
        let objectUrl: string | null = null;
        let fallbackActive = false;
        const fetchController = new AbortController();
        const originalCurrentTimeDescriptor = Object.getOwnPropertyDescriptor(HTMLMediaElement.prototype, 'currentTime');
        const originalDurationDescriptor = Object.getOwnPropertyDescriptor(HTMLMediaElement.prototype, 'duration');

        const clearPlaybackListeners = () => {
            cleanupPlaybackListeners();
            cleanupPlaybackListeners = () => {};
        };

        const startDirectPlayback = (startTime = 0, shouldPlay = false) => {
            clearPlaybackListeners();

            const clampedTime = Math.max(0, Math.min(startTime, Math.max(totalDuration - 0.001, 0)));
            let activeSegmentIndex = segmentIndexAt(clampedTime, cumulativeStarts);
            let suppressVirtualSeek = false;
            let virtualCurrentTime = clampedTime;

            const getNativeCurrentTime = () => {
                if (!originalCurrentTimeDescriptor?.get) return 0;
                return originalCurrentTimeDescriptor.get.call(videoEl);
            };

            const setNativeCurrentTime = (time: number) => {
                if (!originalCurrentTimeDescriptor?.set) return;
                originalCurrentTimeDescriptor.set.call(videoEl, time);
            };

            const setVirtualCurrentTime = (time: number) => {
                virtualCurrentTime = Math.max(0, Math.min(time, Math.max(totalDuration - 0.001, 0)));
            };

            Object.defineProperty(videoEl, 'duration', {
                configurable: true,
                get: () => totalDuration,
            });

            Object.defineProperty(videoEl, 'currentTime', {
                configurable: true,
                get: () => virtualCurrentTime,
                set: (time: number) => {
                    const nextGlobalTime = Math.max(0, Math.min(time, Math.max(totalDuration - 0.001, 0)));
                    const targetSegmentIndex = segmentIndexAt(nextGlobalTime, cumulativeStarts);
                    const localTime = Math.max(0, nextGlobalTime - cumulativeStarts[targetSegmentIndex]);

                    setVirtualCurrentTime(nextGlobalTime);

                    if (suppressVirtualSeek) return;

                    if (targetSegmentIndex === activeSegmentIndex) {
                        setNativeCurrentTime(Math.min(localTime, Math.max(segments[targetSegmentIndex].duration - 0.001, 0)));
                        return;
                    }

                    loadSegment(targetSegmentIndex, localTime, !videoEl.paused);
                },
            });

            videoEl.dispatchEvent(new Event('durationchange'));

            const loadSegment = (segmentIndex: number, localTime: number, autoPlay: boolean) => {
                if (destroyed || segmentIndex < 0 || segmentIndex >= segments.length) return;

                activeSegmentIndex = segmentIndex;
                suppressVirtualSeek = true;
                videoEl.src = segments[segmentIndex].src;
                videoEl.load();

                const handleLoadedMetadata = () => {
                    if (destroyed) return;

                    const segmentDuration = videoEl.duration || segments[segmentIndex].duration;
                    const nextTime = Math.min(localTime, Math.max(segmentDuration - 0.001, 0));
                    setNativeCurrentTime(nextTime);
                    setVirtualCurrentTime(cumulativeStarts[segmentIndex] + nextTime);

                    const handleSeeked = () => {
                        if (destroyed) return;

                        suppressVirtualSeek = false;
                        setVirtualCurrentTime(cumulativeStarts[segmentIndex] + getNativeCurrentTime());

                        if (autoPlay) {
                            void videoEl.play().catch((err) => {
                                console.error('SegmentedVideoPlayer:', err);
                            });
                        }
                    };

                    videoEl.addEventListener('seeked', handleSeeked, { once: true });
                };

                videoEl.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
            };

            const handleTimeUpdate = () => {
                if (destroyed) return;
                setVirtualCurrentTime(cumulativeStarts[activeSegmentIndex] + getNativeCurrentTime());
            };

            const handleEnded = () => {
                if (destroyed) return;
                if (activeSegmentIndex >= segments.length - 1) return;
                loadSegment(activeSegmentIndex + 1, 0, true);
            };

            videoEl.addEventListener('timeupdate', handleTimeUpdate);
            videoEl.addEventListener('ended', handleEnded);
            cleanupPlaybackListeners = () => {
                videoEl.removeEventListener('timeupdate', handleTimeUpdate);
                videoEl.removeEventListener('ended', handleEnded);

                if (originalDurationDescriptor) {
                    Object.defineProperty(videoEl, 'duration', originalDurationDescriptor);
                } else {
                    delete (videoEl as HTMLVideoElement & { duration?: number }).duration;
                }

                if (originalCurrentTimeDescriptor) {
                    Object.defineProperty(videoEl, 'currentTime', originalCurrentTimeDescriptor);
                } else {
                    delete (videoEl as HTMLVideoElement & { currentTime?: number }).currentTime;
                }
            };

            const localTime = Math.max(0, clampedTime - cumulativeStarts[activeSegmentIndex]);
            loadSegment(activeSegmentIndex, localTime, shouldPlay);
        };

        const fallbackToDirectPlayback = (startTime?: number) => {
            if (destroyed || fallbackActive) return;

            fallbackActive = true;
            fetchController.abort();
            clearPlaybackListeners();

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
                objectUrl = null;
            }

            const resumeTime = startTime ?? videoEl.currentTime ?? 0;
            startDirectPlayback(resumeTime, !videoEl.paused);
        };

        const startMsePlayback = async () => {
            if (typeof MediaSource === 'undefined') {
                startDirectPlayback(0, false);
                return;
            }

            const ms = new MediaSource();
            objectUrl = URL.createObjectURL(ms);
            videoEl.src = objectUrl;

            const handleSourceOpen = async () => {
                if (destroyed || fallbackActive) return;

                try {
                    const mimeType = await detectMimeType(segments[0].src);

                    if (!MediaSource.isTypeSupported(mimeType)) {
                        fallbackToDirectPlayback(0);
                        return;
                    }

                    const sb = ms.addSourceBuffer(mimeType);
                    sb.mode = 'sequence';

                    try {
                        ms.duration = totalDuration;
                    } catch {
                        // Ignore duration assignment errors and continue.
                    }

                    for (const segment of segments) {
                        if (destroyed || fallbackActive || fetchController.signal.aborted) return;

                        const response = await fetch(segment.src, { signal: fetchController.signal });
                        const buffer = await response.arrayBuffer();

                        if (destroyed || fallbackActive || fetchController.signal.aborted) return;

                        await waitForIdle(sb);
                        await appendBuffer(sb, buffer);
                    }

                    if (!destroyed && !fallbackActive && ms.readyState === 'open' && !sb.updating) {
                        ms.endOfStream();
                    }
                } catch (err) {
                    if (destroyed || fetchController.signal.aborted) return;

                    console.warn('SegmentedVideoPlayer: falling back to direct playback', err);
                    fallbackToDirectPlayback(videoEl.currentTime || 0);
                }
            };

            ms.addEventListener('sourceopen', handleSourceOpen, { once: true });
        };

        startMsePlayback();

        return () => {
            destroyed = true;
            fetchController.abort();
            clearPlaybackListeners();

            player.destroy();
            videoEl.removeAttribute('src');
            videoEl.load();

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [segments, controls, keyboard, muted, poster]);

    return (
        <div className={className} style={style}>
            <video ref={videoRef} />
        </div>
    );
});

export default SegmentedVideoPlayer;
